import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdEmail, MdPassword, MdOutlineCorporateFare } from 'react-icons/md';
import { FcGoogle } from 'react-icons/fc';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../../context/AuthContext';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const LoginContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const GraphicSection = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2rem;
  color: white;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const LoginForm = styled.div`
  background: white;
  padding: 4rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  animation: ${fadeIn} 0.6s ease-out;

  @media (max-width: 480px) {
    padding: 2rem;
  }
`;

const Title = styled.h1`
  color: #2d3748;
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const InputContainer = styled.div`
  position: relative;
  margin-bottom: 2rem;
`;

const Input = styled.input`
  width: 90%;
  padding: 1rem 1rem 1rem 3rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
  }

  &::placeholder {
    color: #a0aec0;
  }
`;

const InputIcon = styled.span`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  font-size: 1.2rem;
`;

const Button = styled.button`
  width: 100%;
  padding: 1rem;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: #764ba2;
    transform: translateY(-2px);
  }
`;

const GoogleButton = styled(Button)`
  background: white;
  color: #2d3748;
  border: 2px solid #e2e8f0;

  &:hover {
    background: #f7fafc;
    transform: translateY(-2px);
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 2rem 0;
  color: #a0aec0;

  &::before, &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #e2e8f0;
  }

  &::before {
    margin-right: 1rem;
  }

  &::after {
    margin-left: 1rem;
  }
`;

const AdditionalOptions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #4a5568;
  cursor: pointer;
`;

const ForgotPassword = styled.a`
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;

  &:hover {
    color: #764ba2;
  }
`;

const Login = () => {
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
  
    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');
      
      try {
        await login(email, password);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    const ErrorText = styled.div`
    color: #e53e3e;
    margin-bottom: 1rem;
    font-size: 0.875rem;
  `;

  
    // Add error display in your JSX
    {error && <ErrorText>{error}</ErrorText>}
    // Add the styled component for error text
  
  return (
    <LoginContainer>
      <GraphicSection>
        <MdOutlineCorporateFare size={80} />
        <h2>OCR Processing Platform</h2>
        <p>Streamline your invoice processing, GRNs, and purchase orders management</p>
      </GraphicSection>

      <LoginForm>
        <Title>
          <MdOutlineCorporateFare size={32} />
          Organization Login
        </Title>

        <form onSubmit={handleSubmit}>
          <InputContainer >
            <InputIcon>
              <MdEmail size={24} />
            </InputIcon>
            <Input
              type="email"
              placeholder="Enter organization email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </InputContainer>

          <InputContainer>
            <InputIcon>
              <MdPassword size={24} />
            </InputIcon>
            <Input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </InputContainer>

          <AdditionalOptions style={{marginBottom:'10px'}}>
            <CheckboxLabel>
              <input type="checkbox" />
              Remember me
            </CheckboxLabel>
            <ForgotPassword href="#">Forgot Password?</ForgotPassword>
          </AdditionalOptions>

          <Button type="submit" disabled={loading}>
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>

          <Divider>or continue with</Divider>

          {/* <GoogleButton type="button">
            <FcGoogle size={20} />
            Google Account
          </GoogleButton> */}
        </form>
      </LoginForm>
    </LoginContainer>
  );
};

export default Login;